import { httpRequest } from "@services/http";

import {
    toQuery, type ExcludedRow, type HoldoutRow, type ReportScope,
} from "@/modules/holdout/holdoutReport";

/**
 * The Holdout Test Results report (MPN-1688).
 *
 * One request for both tabs, because the Overview is computed from the same rows the By
 * Campaign tab lists. Two requests could return a row on one and not the other if somebody
 * excluded it in between, and the tiles would disagree with the table underneath them.
 */
export interface PooledRow {
    key?: string;
    label?: string;
    /** The lifecycle category this row sits in. Sent on the Use Case and Target Action
        rollups; the Funnel Stage rollup carries it as the row's own `label` instead. */
    funnel_stage_label?: string;
    campaigns?: number;
    treatment_size: number;
    treatment_conversions: number;
    control_size: number;
    control_conversions: number;
    treatment_rate: number | null;
    control_rate: number | null;
    absolute_lift: number | null;
    absolute_lift_lower: number | null;
    absolute_lift_upper: number | null;
    relative_lift: number | null;
    relative_lift_lower: number | null;
    relative_lift_upper: number | null;
    p_value: number | null;
    tier: string;
    srm_check?: string;
}

export interface HoldoutReport {
    tiles: Record<string, number | null>;
    overall: PooledRow;
    by_funnel_stage: PooledRow[];
    by_use_case: PooledRow[];
    by_target_action: PooledRow[];
    rows: HoldoutRow[];
    /** The rows left out, with their statistics and why. Same response as the tiles, because
        they are the reason the tiles read as they do. */
    excluded: ExcludedRow[];
    /** Channel lift (MPN-2060), apart from the population lift above. Empty when the
        `channel-holdout` flag is off; absent on an older server. */
    channel_holdouts?: ChannelHoldoutRow[];
}

/**
 * One (population, channel, Target Action) with a channel holdout (MPN-2060). Both arms are
 * treatment: exposed was served on the channel, held out was not. Neither is the
 * population's control group.
 */
export interface ChannelHoldoutRow {
    population_segment_id: number;
    population: string;
    client: string;
    client_id: number;
    channel: string;
    channel_label: string;
    pct: number;
    journey_step_id: number;
    target_action: string;
    dimensions: number;
    /** The day this channel's test started -- the earliest as_of this population was ever
     *  drawn on for the channel (MPN-2183). The conversions below are counted from it, not
     *  from the campaign's go-live date, and it is carried out of the query that applied it
     *  so the date shown is the date the counts used. Null when nobody was ever held out. */
    channel_start: string | null;
    exposed_size: number;
    exposed_conversions: number;
    exposed_rate: number | null;
    held_out_size: number;
    held_out_conversions: number;
    held_out_rate: number | null;
    absolute_lift: number | null;
    relative_lift: number | null;
    p_value: number | null;
    tier: string;
    statistical_power: string;
}

const BASE = `${import.meta.env.VITE_API_PLATFORM_SERVICE_URL}api/v1/analytics/holdout-report`;

export async function getHoldoutReport(scope: ReportScope = {}): Promise<HoldoutReport> {
    // `toQuery` drops the blanks and the half-typed dates -- a date input reports "2026-09"
    // mid-entry, and sending that would have the server reject the request while somebody is
    // still typing.
    const query = new URLSearchParams(toQuery(scope));
    const suffix = query.toString() ? `?${query}` : "";
    return httpRequest<HoldoutReport>(`${BASE}/${suffix}`, { method: "GET" });
}

export async function excludeRow(payload: {
    launch_id: number;
    journey_step_id?: number | null;
    is_excluded: boolean;
    reason?: string;
    detail?: string;
}) {
    return httpRequest(`${BASE}/exclusions/`, { method: "POST", body: payload });
}

/** One row of the Use Case Mapping tab (MPN-1688's reference tab). */
export interface UseCaseMapping {
    id: number;
    vertical: string;
    code: string;
    name: string;
    funnel_stage: string;
    is_active: boolean;
    /** How many campaigns hold this code. Zero is a safe retirement; forty is not. */
    campaigns: number;
    /** The funnel stage is part of what this use case means, so it is not an admin's to
        change. Sent by the server rather than decided here: a rule the server enforces and
        the client guesses at disagrees with itself the first time either changes, and the
        disagreement shows up as a select that looks broken. */
    stage_locked: boolean;
}

export async function getUseCaseMapping(): Promise<UseCaseMapping[]> {
    return httpRequest<UseCaseMapping[]>(`${BASE}/use-case-mapping/`, { method: "GET" });
}

export async function updateUseCaseMapping(
    id: number, patch: { name?: string; funnel_stage?: string; is_active?: boolean },
) {
    return httpRequest(`${BASE}/use-case-mapping/${id}/`, { method: "PATCH", body: patch });
}

export type { ExcludedRow, ReportScope };
